import type { Instance as PopperInstance } from '@popperjs/core';
import { Modifier } from 'react-popper';

import { isBrowser as _isBrowser } from '../../core/utils/isBrowser.js';
import type {
  HeightStrategy,
  OverlaySizeStrategyRange,
  WidthStrategy,
} from '../types/sizing.types.js';

type PopperEnrichedElement = HTMLElement & {
  __popperInstance__: PopperInstance;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isPopperEnrichedElement(value: any): value is PopperEnrichedElement {
  return value !== undefined && value['__popperInstance__'] !== undefined;
}

/**
 * Check if widthStrategy is OverlaySizeStrategyRange
 * @internal
 */
export function isOverlaySizeStrategyRange(
  widthStrategy: WidthStrategy,
): widthStrategy is OverlaySizeStrategyRange {
  return (
    (widthStrategy as OverlaySizeStrategyRange).min !== undefined ||
    (widthStrategy as OverlaySizeStrategyRange).max !== undefined
  );
}

/**
 * Popper.js modifier which listens to the overlay element size and calls update
 * on the popper instance to reposition the element correctly.
 * @internal
 */
export const observeOverlaySize: Modifier<
  'observeOverlaySize',
  { resizeObserverInstance: ResizeObserver | false }
> = {
  name: 'observeOverlaySize',
  enabled: true,
  phase: 'main',
  options: {
    resizeObserverInstance:
      _isBrowser &&
      window.ResizeObserver &&
      new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { target } = entry;
          if (isPopperEnrichedElement(target)) {
            void target['__popperInstance__'].update();
          }
        }
      }),
  },
  // needed a function even though it seems optional
  fn: () => undefined,
  effect: ({ state, options, instance }) => {
    const { popper: overlayElement, reference } = state.elements;
    const { resizeObserverInstance } = options;
    if (resizeObserverInstance instanceof ResizeObserver) {
      resizeObserverInstance?.observe(overlayElement);
      (overlayElement as PopperEnrichedElement)['__popperInstance__'] =
        instance;

      // Also listen to the trigger size and adjust position
      if (reference instanceof HTMLElement) {
        resizeObserverInstance?.observe(reference);
        (reference as PopperEnrichedElement)['__popperInstance__'] = instance;
      }

      return () => {
        resizeObserverInstance?.unobserve(overlayElement);
        if (isPopperEnrichedElement(overlayElement)) {
          delete (overlayElement as Partial<PopperEnrichedElement>)[
            '__popperInstance__'
          ];
        }
        if (reference instanceof HTMLElement) {
          resizeObserverInstance?.unobserve(reference);
          if (isPopperEnrichedElement(overlayElement)) {
            delete (reference as Partial<PopperEnrichedElement>)[
              '__popperInstance__'
            ];
          }
        }
      };
    }
  },
};

/**
 * Popper.js modifier which listens to the animation done event
 * to update the popper instance position.

* @internal
*/
export const observeAnimationDone: Modifier<'observeAnimationDone'> = {
  name: 'observeAnimationDone',
  enabled: true,
  phase: 'main',
  // needed a function even though it seems optional (throws a console error otherwise)
  fn: () => undefined,
  effect: ({ state, instance }) => {
    const { popper: overlayElement } = state.elements;
    (overlayElement as PopperEnrichedElement)['__popperInstance__'] = instance;

    const updatePosition = () => {
      if (isPopperEnrichedElement(overlayElement)) {
        requestAnimationFrame(() => {
          void instance.update();
        });
      }
    };

    window?.addEventListener('animationDone', updatePosition, false);

    return () => {
      window?.removeEventListener('animationDone', updatePosition, false);

      if (isPopperEnrichedElement(overlayElement)) {
        delete (overlayElement as Partial<PopperEnrichedElement>)[
          '__popperInstance__'
        ];
      }
    };
  },
};

/**
 * Popper.js modifier which sets the popper max and/or min width of the overlay

* @internal
*/
export const overlayWidthStrategyHandling: Modifier<
  'overlayWidthStrategyHandling',
  { widthStrategy: WidthStrategy }
> = {
  name: 'overlayWidthStrategyHandling',
  fn: ({ state, options }) => {
    const widthStrategy = options.widthStrategy as WidthStrategy;

    if (widthStrategy === 'trigger') {
      state.styles.popper.width = `${state.rects.reference.width}px`;
    } else if (isOverlaySizeStrategyRange(widthStrategy)) {
      state.styles.popper.minWidth = `${widthStrategy.min || 0}px`;
      state.styles.popper.maxWidth =
        widthStrategy.max !== undefined ? `${widthStrategy.max}px` : '100%';
    }
  },
  phase: 'beforeWrite',
  requires: ['computeStyles'],
};

/**
 * Popper.js modifier which sets the popper max height of the overlay

* @internal
*/
export const overlayHeightStrategyHandling: Modifier<
  'overlayHeightStrategyHandling',
  { heightStrategy: HeightStrategy }
> = {
  name: 'overlayHeightStrategyHandling',
  fn: ({ state, options }): void => {
    const heightStrategy = options.heightStrategy as HeightStrategy;
    if (heightStrategy === 'maxViewport') {
      let maxHeight = state.rects.reference.y;
      // add 10px spacing so the overlay does not extend all the way to the edges of the screen
      const spacing = 10;
      const viewportHeight = Math.max(
        document.documentElement.clientHeight || 0,
        window.innerHeight || 0,
      );
      if (maxHeight < viewportHeight / 2) {
        maxHeight = viewportHeight - maxHeight - state.rects.reference.height;
      }
      state.styles.popper.maxHeight = `${maxHeight - spacing}px`;
    } else if (isOverlaySizeStrategyRange(heightStrategy)) {
      state.styles.popper.minHeight = `${heightStrategy.min || 0}px`;
      state.styles.popper.maxHeight =
        heightStrategy.max !== undefined ? `${heightStrategy.max}px` : '100%';
    }
  },
  phase: 'beforeWrite',
  requires: ['computeStyles'],
};
